import { motion, useReducedMotion } from "motion/react";
import { Anchor, ArrowDown, UsersThree } from "@phosphor-icons/react";
import { APPLICATION_URL, heroCopy } from "../../content";
import { useGame } from "../../game/GameContext";
import { Button } from "../ui/Button";
import { AutterAnchor, AutterWordmark, CaptainPatchImage } from "../brand/AutterBrand";

const EASE = [0.16, 1, 0.3, 1] as const;

export function Hero({ onPlay }: { onPlay?: () => void }) {
  const reduce = useReducedMotion();
  const { start } = useGame();
  const rise = (delay: number) => ({
    initial: reduce ? false : { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.75, delay, ease: EASE },
  });

  return (
    <section id="top" className="chart-grid relative min-h-[100dvh] overflow-hidden bg-paper pt-24">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_70%_42%,rgba(255,255,255,0.7),transparent_42%)]" />
      <AutterAnchor className="pointer-events-none absolute -bottom-52 -left-44 w-[34rem] rotate-12 opacity-[0.035]" />

      <div className="relative mx-auto grid w-full max-w-[1480px] items-center gap-12 px-5 py-14 md:px-10 lg:grid-cols-[0.78fr_1.22fr] lg:gap-10 lg:py-16">
        <div className="relative z-10 lg:py-10">
          <motion.div {...rise(0)} className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-line bg-foam px-3 py-1.5 shadow-card">
              <AutterWordmark className="h-4 w-auto" />
              <span className="font-mono text-[9px] tracking-[0.12em] text-muted uppercase">presents</span>
            </span>
            <p className="font-mono text-xs tracking-[0.12em] text-muted uppercase">{heroCopy.kicker}</p>
          </motion.div>
          <motion.h1 {...rise(0.08)} className="display-type-wide mt-4 text-[clamp(4.4rem,10vw,9.5rem)] text-ink">
            Same
            <br />
            Boat
          </motion.h1>
          <motion.p {...rise(0.16)} className="mt-7 max-w-[35ch] text-lg leading-snug font-medium text-ink-soft md:text-xl">
            {heroCopy.sub}
          </motion.p>
          <motion.div {...rise(0.24)} className="mt-9 flex flex-wrap items-center gap-6">
            <Button href="#boats" size="lg" arrow="down" onClick={start}>
              {heroCopy.primaryCta}
            </Button>
            <a
              href={APPLICATION_URL}
              {...(APPLICATION_URL.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})}
              className="text-sm font-semibold text-ink-soft underline underline-offset-4 transition-colors hover:text-ink"
            >
              {heroCopy.secondaryCta}
            </a>
          </motion.div>

          <motion.div {...rise(0.32)} className="mt-12 flex max-w-md gap-6 border-t border-line pt-5 font-mono text-[11px] leading-relaxed text-muted">
            <p className="flex items-start gap-2"><Anchor weight="bold" className="mt-0.5 size-4 shrink-0 text-signal" />Different boats.</p>
            <p className="flex items-start gap-2"><UsersThree weight="bold" className="mt-0.5 size-4 shrink-0 text-signal" />Shared water.</p>
          </motion.div>
        </div>

        <motion.figure
          initial={reduce ? false : { opacity: 0, x: 60, rotate: 1.5 }}
          animate={{ opacity: 1, x: 0, rotate: -0.5 }}
          transition={{ duration: 1, delay: 0.15, ease: EASE }}
          className="group relative lg:-mr-24"
        >
          <div className="relative aspect-[3/2] overflow-hidden rounded-[0.4rem] border-2 border-ink bg-sea-soft shadow-lift">
            <motion.img
              src="/page-assets/harbour-voyage.jpg"
              alt="An orange-sailed boat follows a winding route past messages and a stranded builder toward a lively communal harbour."
              className="h-full w-full object-cover"
              animate={reduce ? undefined : { scale: [1, 1.025, 1] }}
              transition={reduce ? undefined : { duration: 18, repeat: Infinity, ease: "easeInOut" }}
            />
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,transparent_65%,rgba(17,30,43,0.48))]" />
            <motion.button
              type="button"
              onClick={onPlay}
              initial={false}
              whileHover={{ scale: 1.01, transition: { duration: 0.2, ease: EASE } }}
              whileTap={{ scale: 0.99, transition: { duration: 0.1 } }}
              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-[0.4rem] bg-night/70 opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100 motion-reduce:opacity-0 motion-reduce:hover:opacity-100"
              style={{
                background: 'linear-gradient(135deg, oklch(23% 0.034 252 / 0.85), oklch(29% 0.036 250 / 0.75))',
              }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.8, rotate: -12 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ delay: 0.05, duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
              >
                <motion.svg
                  animate={reduce ? undefined : { rotate: [0, 3, -3, 0] }}
                  transition={reduce ? undefined : { duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="size-10 text-signal"
                  viewBox="0 0 256 256"
                  aria-hidden
                >
                  <path
                    fill="currentColor"
                    d="M128 24C67.1 24 18 73.1 18 134c0 25.3 8.5 48.7 22.9 67.1l50.1 50.1c4.7 4.7 12.3 4.7 17 0l50.1-50.1C229.5 182.7 238 159.3 238 134c0-60.9-49.1-110-110-110zm0 196c-47.4 0-86-38.6-86-86 0-19.6 6.4-37.7 17-52l36 36c-8.2 10.6-13.1 23.5-13.1 37 0 32.3 26.3 58.6 58.6 58.6 13.5 0 26.4-4.9 37-13.1l36 36c-14.3 10.6-32.4 17-52 17z"
                  />
                  <circle cx="128" cy="128" r="36" fill="none" stroke="currentColor" strokeWidth="8" strokeLinecap="round" strokeDasharray="90 180" />
                  <line x1="128" y1="128" x2="128" y2="56" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
                  <line x1="128" y1="128" x2="200" y2="200" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
                </motion.svg>
              </motion.div>
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.5, ease: EASE }}
                className="font-display text-2xl font-extrabold tracking-tight text-foam uppercase md:text-3xl"
              >
                Play the game
              </motion.span>
              <motion.span
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25, duration: 0.5, ease: EASE }}
                className="relative font-mono text-xs tracking-wider text-foam/70 uppercase"
              >
                Set sail
                <motion.span
                  animate={reduce ? undefined : { x: [0, 4, 0] }}
                  transition={reduce ? undefined : { duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
                  className="inline-block ml-1"
                >
                  →
                </motion.span>
              </motion.span>
            </motion.button>
            <motion.figcaption
              initial={reduce ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.6, ease: EASE }}
              className="absolute right-5 bottom-5 left-5 flex items-end justify-between gap-6 text-foam"
            >
              <p className="max-w-[32ch] text-sm font-medium md:text-base">One orange sail. A harbour full of people solving familiar problems.</p>
              <span className="hidden shrink-0 items-center gap-2 font-mono text-[10px] tracking-wider uppercase sm:flex">
                follow the wake <ArrowDown className="size-4" />
              </span>
            </motion.figcaption>
          </div>

          <motion.div
            animate={reduce ? undefined : { y: [0, -8, 0], rotate: [-2, 0, -2] }}
            transition={reduce ? undefined : { duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-5 left-5 flex items-start gap-3"
          >
            <CaptainPatchImage pose="welcome" className="h-24 w-24 shrink-0 drop-shadow-xl md:h-28 md:w-28" />
            <div className="mt-1 max-w-56 rotate-[-2deg] rounded-2xl rounded-bl-sm border border-ink bg-foam/95 px-4 py-3 shadow-card backdrop-blur">
              <p className="font-mono text-[9px] tracking-[0.14em] text-signal-deep uppercase">Patch's log · 01</p>
              <p className="mt-1 font-hand text-xl leading-tight text-ink">Nobody should cross the hard weeks alone.</p>
            </div>
          </motion.div>
        </motion.figure>
      </div>
    </section>
  );
}
